import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import TrainerLayout from '../../components/layout/TrainerLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Loader2, Heart, Brain, Target, Lock, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIAS = [
  { value: 'CUERPO', label: 'Cuerpo', icon: Heart, color: 'text-red-400' },
  { value: 'MENTE', label: 'Mente', icon: Brain, color: 'text-purple-400' },
  { value: 'PROPOSITO', label: 'Propósito', icon: Target, color: 'text-amber-400' },
];

const TrainerPildorasPage = () => {
  const [pildoras, setPildoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [formData, setFormData] = useState({
    numero_orden: '',
    titulo: '',
    contenido: '',
    categoria: 'CUERPO',
    referencias: ''
  });

  useEffect(() => { fetchPildoras(); }, []);

  const fetchPildoras = async () => {
    try {
      const response = await axios.get(`${API}/pildoras-progresivas`);
      setPildoras(response.data.pildoras || []);
    } catch (error) {
      toast.error('Error al cargar píldoras');
    } finally {
      setLoading(false);
    }
  };

  const myPildoras = useMemo(() => pildoras.filter(p => p.is_mine), [pildoras]);
  const globalPildoras = useMemo(() => pildoras.filter(p => !p.is_mine), [pildoras]);

  const resetForm = () => {
    setFormData({ numero_orden: '', titulo: '', contenido: '', categoria: 'CUERPO', referencias: '' });
    setEditing(null);
  };

  const openNew = () => {
    resetForm();
    // Sugerir el siguiente numero_orden libre para el trainer
    const usedNumbers = new Set(myPildoras.map(p => p.numero_orden));
    let next = 1;
    while (usedNumbers.has(next)) next++;
    setFormData(p => ({ ...p, numero_orden: String(next) }));
    setShowDialog(true);
  };

  const openEdit = (pildora) => {
    if (pildora.read_only) {
      toast.warning('Esta píldora es de GuardiansFit, no se puede editar');
      return;
    }
    setEditing(pildora);
    setFormData({
      numero_orden: String(pildora.numero_orden),
      titulo: pildora.titulo,
      contenido: pildora.contenido,
      categoria: pildora.categoria,
      referencias: pildora.referencias || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.numero_orden || !formData.titulo || !formData.contenido) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        numero_orden: parseInt(formData.numero_orden),
        titulo: formData.titulo,
        contenido: formData.contenido,
        categoria: formData.categoria,
        referencias: formData.referencias
      };
      if (editing) {
        await axios.put(`${API}/pildoras-progresivas/${editing.id}`, payload);
        toast.success('Píldora actualizada');
      } else {
        await axios.post(`${API}/pildoras-progresivas`, payload);
        toast.success('Píldora creada');
      }
      setShowDialog(false);
      resetForm();
      fetchPildoras();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pildora) => {
    if (pildora.read_only) {
      toast.warning('No puedes borrar píldoras de GuardiansFit');
      return;
    }
    if (!window.confirm(`¿Borrar "${pildora.titulo}"?`)) return;
    try {
      await axios.delete(`${API}/pildoras-progresivas/${pildora.id}`);
      toast.success('Píldora eliminada');
      fetchPildoras();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const renderPildoraCard = (pildora) => {
    const cat = CATEGORIAS.find(c => c.value === pildora.categoria) || CATEGORIAS[0];
    const Icon = cat.icon;
    const isExpanded = expandedId === pildora.id;
    return (
      <Card key={pildora.id} className={`bg-card border ${pildora.read_only ? 'border-amber-500/20' : 'border-blue-500/20'}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${cat.color}`} />
                <Badge variant="outline" className="text-xs">#{pildora.numero_orden}</Badge>
                {pildora.read_only ? (
                  <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-400">
                    <Lock className="h-3 w-3 mr-1" />GuardiansFit
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs border-blue-500/40 text-blue-400">Mía</Badge>
                )}
              </div>
              <h3 className="font-semibold text-foreground text-sm">{pildora.titulo}</h3>
              {!isExpanded ? (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {(pildora.contenido || '').replace(/\*\*/g, '')}
                </p>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none mt-2">
                  {(pildora.contenido || '').split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-sm text-foreground/90 mb-3 whitespace-pre-wrap leading-relaxed">
                      {paragraph.split('**').map((part, j) =>
                        j % 2 === 1
                          ? <strong key={j} className="text-emerald-300">{part}</strong>
                          : part
                      )}
                    </p>
                  ))}
                </div>
              )}
              {isExpanded && pildora.referencias && (
                <p className="text-xs text-muted-foreground mt-2 italic border-t border-zinc-800 pt-2">
                  Refs: {pildora.referencias}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => setExpandedId(isExpanded ? null : pildora.id)}
                data-testid={`expand-pildora-${pildora.id}`}>
                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </Button>
              {!pildora.read_only && (
                <>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(pildora)} data-testid={`edit-pildora-${pildora.id}`}>
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(pildora)} data-testid={`delete-pildora-${pildora.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <TrainerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </TrainerLayout>
    );
  }

  return (
    <TrainerLayout>
      <div className="space-y-6 animate-fade-in" data-testid="trainer-pildoras-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              Píldoras del Día
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Mensajes educativos progresivos para tus clientes ({myPildoras.length} tuyas · {globalPildoras.length} de GuardiansFit)
            </p>
          </div>
          <Button onClick={openNew} data-testid="create-pildora-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Píldora
          </Button>
        </div>

        <Tabs defaultValue="mine">
          <TabsList className="bg-zinc-900">
            <TabsTrigger value="mine" data-testid="tab-mine">Mis Píldoras ({myPildoras.length})</TabsTrigger>
            <TabsTrigger value="global" data-testid="tab-global">GuardiansFit ({globalPildoras.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="mine" className="mt-4">
            {myPildoras.length === 0 ? (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Aún no has creado ninguna píldora</p>
                  <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Crear primera píldora</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {myPildoras.map(renderPildoraCard)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="global" className="mt-4">
            <p className="text-xs text-muted-foreground mb-3 italic">
              Estas son las píldoras creadas por GuardiansFit. Puedes consultarlas pero no modificarlas.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {globalPildoras.map(renderPildoraCard)}
            </div>
          </TabsContent>
        </Tabs>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar' : 'Nueva'} Píldora</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Número de orden *</Label>
                  <Input type="number" value={formData.numero_orden}
                    onChange={(e) => setFormData(p => ({ ...p, numero_orden: e.target.value }))}
                    placeholder="1, 2, 3..." className="bg-zinc-900/50 border-zinc-800"
                    data-testid="pildora-numero-input" />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={formData.categoria} onValueChange={(v) => setFormData(p => ({ ...p, categoria: v }))}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Título *</Label>
                <Input value={formData.titulo} onChange={(e) => setFormData(p => ({ ...p, titulo: e.target.value }))}
                  placeholder="Título corto e impactante" className="bg-zinc-900/50 border-zinc-800"
                  data-testid="pildora-titulo-input" />
              </div>
              <div>
                <Label>Contenido *</Label>
                <Textarea value={formData.contenido} onChange={(e) => setFormData(p => ({ ...p, contenido: e.target.value }))}
                  placeholder="Mensaje educativo (recomendado 2-4 párrafos)" rows={6}
                  className="bg-zinc-900/50 border-zinc-800" data-testid="pildora-contenido-input" />
                <p className="text-xs text-muted-foreground mt-1">
                  Tip: usa <code className="px-1 bg-zinc-800 rounded">**texto**</code> para negritas y deja una línea en blanco para separar párrafos.
                </p>
              </div>
              <div>
                <Label>Referencias (opcional)</Label>
                <Input value={formData.referencias} onChange={(e) => setFormData(p => ({ ...p, referencias: e.target.value }))}
                  placeholder="Estudios o fuentes citadas" className="bg-zinc-900/50 border-zinc-800" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="pildora-save-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TrainerLayout>
  );
};

export default TrainerPildorasPage;
